import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EditIcon from '@mui/icons-material/Edit';
import QuizIcon from '@mui/icons-material/Quiz';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface TopicSubtopicCardsProps {
  selectedSubject: string;
  selectedTopic: string;
  selectedSubtopic: string;
  availableSubjects: string[];
  availableTopics: string[];
  availableSubtopics: string[];
  onSubjectSelect: (subject: string) => void;
  onTopicSelect: (topic: string) => void;
  onSubtopicSelect: (subtopic: string) => void;
  onPredefinedQuestionClick: (questionData: any, type: 'doubt' | 'test') => void;
  onEditSubject?: () => void;
  onEditTopic?: () => void;
  onEditSubtopic?: () => void;
  filteredQuestions?: any[];
}

const TOPIC_COLORS = [
  { bg: '#2196F3', border: '#2196F3', text: '#FFFFFF' },
  { bg: '#9C27B0', border: '#9C27B0', text: '#FFFFFF' },
  { bg: '#4CAF50', border: '#4CAF50', text: '#FFFFFF' },
  { bg: '#FF9800', border: '#FF9800', text: '#FFFFFF' },
  { bg: '#E91E63', border: '#E91E63', text: '#FFFFFF' },
  { bg: '#009688', border: '#009688', text: '#FFFFFF' },
  { bg: '#8BC34A', border: '#8BC34A', text: '#FFFFFF' },
  { bg: '#FF5722', border: '#FF5722', text: '#FFFFFF' },
];

const TopicSubtopicCards: React.FC<TopicSubtopicCardsProps> = ({
  selectedSubject,
  selectedTopic,
  selectedSubtopic,
  availableSubjects,
  availableTopics,
  availableSubtopics,
  onSubjectSelect,
  onTopicSelect,
  onSubtopicSelect,
  onPredefinedQuestionClick,
  onEditSubject,
  onEditTopic,
  onEditSubtopic,
  filteredQuestions = [],
}) => {

  // Show subjects when no subject is selected
  if (!selectedSubject) {
    return (
      <Box sx={{
        p: { xs: 1.5, md: 2 },
        maxWidth: '100%',
        overflow: 'hidden',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
        <Typography variant="h6" fontWeight={600} sx={{
          mb: 1.5,
          color: '#2c3e50',
          fontSize: { xs: '1rem', md: '1.1rem' },
          flexShrink: 0,
        }}>
          Select a Subject
        </Typography>

        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: { xs: 'center', sm: 'flex-start' },
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 300px)',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          {availableSubjects.map((subject, index) => {
            const color = TOPIC_COLORS[index % TOPIC_COLORS.length];
            return (
              <Chip
                key={subject}
                label={subject}
                onClick={() => onSubjectSelect(subject)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: color.bg,
                  color: color.text,
                  border: `2px solid ${color.border}`,
                  fontSize: { xs: '12px', md: '13px' },
                  height: { xs: '32px', md: '36px' },
                  px: 1.5,
                  py: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: color.border,
                    color: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${color.border}60`,
                  },
                  '& .MuiChip-label': {
                    px: 1,
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  // Show topics when only subject is selected
  if (selectedSubject && !selectedTopic) {
    return (
      <Box sx={{
        p: { xs: 1.5, md: 2 },
        maxWidth: '100%',
        overflow: 'hidden',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
        {/* Subject Chip with Edit Icon */}
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={selectedSubject}
            sx={{
              bgcolor: '#2196F3',
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '12px', md: '14px' },
              height: { xs: '28px', md: '32px' },
            }}
          />
          <IconButton
            size="small"
            onClick={onEditSubject}
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
            title="Edit Subject"
          >
            <EditIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
          </IconButton>
        </Box>

        <Typography variant="h6" fontWeight={600} sx={{
          mb: 1,
          color: '#2c3e50',
          fontSize: { xs: '1rem', md: '1.1rem' },
          flexShrink: 0,
        }}>
          Select a Topic in {selectedSubject}
        </Typography>

        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: { xs: 'center', sm: 'flex-start' },
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 350px)',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          {availableTopics.map((topic, index) => {
            const color = TOPIC_COLORS[index % TOPIC_COLORS.length];
            return (
              <Chip
                key={topic}
                label={topic}
                onClick={() => onTopicSelect(topic)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: color.bg,
                  color: color.text,
                  border: `2px solid ${color.border}`,
                  fontSize: { xs: '12px', md: '13px' },
                  height: { xs: '32px', md: '36px' },
                  px: 1.5,
                  py: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: color.border,
                    color: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${color.border}60`,
                  },
                  '& .MuiChip-label': {
                    px: 1,
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  // Show subtopics when topic is selected but not subtopic
  if (selectedSubject && selectedTopic && !selectedSubtopic) {
    return (
      <Box sx={{
        p: { xs: 1.5, md: 2 },
        maxWidth: '100%',
        overflow: 'hidden',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
        {/* Subject and Topic Chips with Edit Icons */}
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={selectedSubject}
            sx={{
              bgcolor: '#2196F3',
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '11px', md: '13px' },
              height: { xs: '26px', md: '30px' },
            }}
          />
          <IconButton
            size="small"
            onClick={onEditSubject}
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: { xs: 24, md: 28 },
              height: { xs: 24, md: 28 },
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
            title="Edit Subject"
          >
            <EditIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
          </IconButton>

          <Chip
            label={selectedTopic}
            sx={{
              bgcolor: '#9C27B0',
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '11px', md: '13px' },
              height: { xs: '26px', md: '30px' },
            }}
          />
          <IconButton
            size="small"
            onClick={onEditTopic}
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: { xs: 24, md: 28 },
              height: { xs: 24, md: 28 },
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
            title="Edit Topic"
          >
            <EditIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
          </IconButton>
        </Box>

        <Box sx={{ flexShrink: 0, mb: 1.5 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5, color: '#2c3e50', fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Select a Subtopic
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '11px', md: '13px' } }}>
            Choose from the available subtopics below
          </Typography>
        </Box>

        <Box sx={{
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 380px)',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          {/* Subtopics as Chips */}
          {availableSubtopics.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#444', fontSize: { xs: '12px', md: '13px' } }}>
                📚 Subtopics
              </Typography>
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                maxWidth: '100%'
              }}>
                {availableSubtopics.map((subtopic, index) => {
                  const color = TOPIC_COLORS[(index + 2) % TOPIC_COLORS.length];
                  return (
                    <Chip
                      key={subtopic}
                      label={subtopic}
                      onClick={() => onSubtopicSelect(subtopic)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: color.bg,
                        color: color.text,
                        border: `1.5px solid ${color.border}`,
                        fontSize: { xs: '11px', md: '12px' },
                        height: { xs: '28px', md: '32px' },
                        px: 1.25,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: color.border,
                          color: '#fff',
                          transform: 'translateY(-2px)',
                          boxShadow: `0 3px 6px ${color.border}60`,
                        },
                        '& .MuiChip-label': {
                          px: 0.75,
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // Show questions when all are selected (subject, topic, subtopic)
  if (selectedSubject && selectedTopic && selectedSubtopic) {
    return (
      <Box sx={{
        p: { xs: 1.5, md: 2 },
        maxWidth: '100%',
        overflow: 'hidden',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
        {/* All Chips with Edit Icons */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={selectedSubject}
            sx={{
              bgcolor: '#2196F3',
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '11px', md: '13px' },
              height: { xs: '26px', md: '30px' },
            }}
          />
          <IconButton
            size="small"
            onClick={onEditSubject}
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: { xs: 24, md: 28 },
              height: { xs: 24, md: 28 },
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
            title="Edit Subject"
          >
            <EditIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
          </IconButton>

          <Chip
            label={selectedTopic}
            sx={{
              bgcolor: '#9C27B0',
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '11px', md: '13px' },
              height: { xs: '26px', md: '30px' },
            }}
          />
          <IconButton
            size="small"
            onClick={onEditTopic}
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: { xs: 24, md: 28 },
              height: { xs: 24, md: 28 },
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
            title="Edit Topic"
          >
            <EditIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
          </IconButton>

          <Chip
            label={selectedSubtopic}
            sx={{
              bgcolor: '#4CAF50',
              color: '#fff',
              fontWeight: 600,
              fontSize: { xs: '11px', md: '13px' },
              height: { xs: '26px', md: '30px' },
            }}
          />
          <IconButton
            size="small"
            onClick={onEditSubtopic}
            sx={{
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              width: { xs: 24, md: 28 },
              height: { xs: 24, md: 28 },
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
            title="Edit Subtopic"
          >
            <EditIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
          </IconButton>
        </Box>

        {/* Questions Section */}
        <Box sx={{
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 320px)',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}>
          {filteredQuestions && filteredQuestions.length > 0 ? (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: '#444', fontSize: { xs: '13px', md: '14px' } }}>
                💡 Suggested Questions
              </Typography>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}>
                {filteredQuestions.map((item, index) => {
                  // Extract question from nested entity object
                  const questionText = item?.entity?.question || item?.question || 'Question not available';
                  const isDoubt = true;

                  return (
                    <Chip
                      key={index}
                      icon={isDoubt ? <HelpOutlineIcon /> : <QuizIcon />}
                      label={questionText}
                      onClick={() => onPredefinedQuestionClick(item, 'doubt')} // Pass entire item object instead of just text
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isDoubt ? '#FFF3E0' : '#E3F2FD',
                        color: isDoubt ? '#E65100' : '#1565C0',
                        border: `1.5px solid ${isDoubt ? '#FF9800' : '#2196F3'}`,
                        fontSize: { xs: '12px', md: '13px' },
                        height: 'auto',
                        minHeight: { xs: '36px', md: '40px' },
                        px: 1.5,
                        py: 1,
                        justifyContent: 'flex-start',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: isDoubt ? '#FFE0B2' : '#BBDEFB',
                          transform: 'translateX(4px)',
                          boxShadow: `0 2px 8px ${isDoubt ? '#FF980040' : '#2196F340'}`,
                        },
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          textAlign: 'left',
                          py: 0.5,
                          display: 'block',
                          width: '100%',
                        },
                        '& .MuiChip-icon': {
                          fontSize: { xs: 16, md: 18 },
                          color: isDoubt ? '#FF9800' : '#2196F3',
                          marginLeft: 0.5,
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </>
          ) : null}
        </Box>

        {/* Call to Action */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 1,
          mt: 2,
          bgcolor: '#E3F2FD',
          p: { xs: 1.5, md: 2 },
          borderRadius: 2,
          border: '2px dashed #2196F3',
        }}>
          <ChatBubbleOutlineIcon sx={{ color: '#2196F3', fontSize: { xs: 18, md: 22 } }} />
          <Typography
            variant="body2"
            sx={{
              color: '#1565C0',
              fontWeight: 600,
              fontSize: { xs: '12px', md: '13px' },
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            Or ask your own question below
          </Typography>
        </Box>
      </Box>
    );
  }

  return null;
};

export default TopicSubtopicCards;